import {
  OnInit,
  effect,
  Component,
  ElementRef,
  AfterViewInit,
  Input,
  OnDestroy,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import lottie, { AnimationItem } from 'lottie-web';
import { ThemeService } from '@nebula-shared/data-access';
import nebulaAnimationDark from './nebula-dark.json';
import nebulaAnimationLight from './nebula-light.json';

@Component({
  selector: 'app-loader',
  templateUrl: './loader.component.html',
  styleUrls: ['./loader.component.scss'],
})
export class LoaderComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly tema = inject(ThemeService);

  @Input() duration = 3000;
  /** Texto de estado (i18n). Lo pasa el padre traducido; si no, usa el valor por defecto. */
  @Input() statusText?: string;
  @Output() completed = new EventEmitter<void>();

  @ViewChild('animationContainer', { static: false })
  readonly animationContainer!: ElementRef<HTMLDivElement>;

  private animation: AnimationItem | null = null;
  progress = 0;
  progressWidth = '0%';
  loadingStatus = 'Progreso en Curso ...';
  private progressInterval: number | null = null;
  private statusInterval: number | null = null;
  isDarkMode = false;
  private readonly cdr = inject(ChangeDetectorRef);

  private statusMessages: string[] = ['Progreso en Curso ...'];

  constructor() {
    this.checkTheme();
  }

  /** Aplica el texto de estado traducido que envia el padre (si lo hay). */
  /**
   * Pone el rotulo que le hayan dado, ANTES del primer pintado.
   *
   * Si se hiciera despues -al terminar de montar la vista- Angular veria que el
   * texto cambio entre lo que comprobo y lo que se pinta, y avisaria de ello: la
   * vista ya se habria dibujado con el rotulo por defecto.
   */
  ngOnInit(): void {
    this.applyStatusText();
  }

  private applyStatusText(): void {
    if (this.statusText) {
      this.loadingStatus = this.statusText;
      this.statusMessages = [this.statusText];
    }
  }

  /**
   * Si el pie de carga se ve oscuro.
   *
   * Se pregunta al documento y no al almacenamiento: quien decide el tema es el
   * dueño del tema, y lo que hace es poner o quitar esta clase. Leyendo la clase,
   * el pie acierta siempre -incluso en el instante de entrada, cuando el tema del
   * usuario acaba de aplicarse y lo guardado aun no se ha leido-.
   */
  private checkTheme(): void {
    this.isDarkMode =
      typeof document !== 'undefined' && document.documentElement.classList.contains('dark-theme');
  }

  ngAfterViewInit(): void {
    this.initializeAnimation();
    this.startProgressBar();
    this.startStatusRotation();
  }

  /**
   * Repinta la animacion cuando cambia el tema.
   *
   * Antes escuchaba el evento `storage`, que el navegador solo dispara en LAS OTRAS
   * pestañas: en la propia no llegaba nunca, que es justo donde ocurre el cambio de
   * turno. Ahora se entera por el dueño del tema y acierta en las dos.
   */
  private readonly repinteAlCambiarTema = effect(() => {
    const oscuro = this.tema.oscuro();
    if (this.isDarkMode === oscuro) return;

    this.isDarkMode = oscuro;
    this.reloadAnimation();
    this.cdr.detectChanges();
  });

  private reloadAnimation(): void {
    if (this.animation) {
      this.animation.destroy();
      this.initializeAnimation();
    }
  }

  private startProgressBar(): void {
    const step = 100;
    const totalSteps = this.duration / step;
    const incrementPerStep = 100 / totalSteps;

    this.progress = 0;

    this.progressInterval = window.setInterval(() => {
      if (this.progress < 100) {
        this.progress = Math.min(this.progress + incrementPerStep, 100);
        this.progressWidth = this.progress + '%';
        this.cdr.detectChanges();
      } else {
        this.completeLoading();
      }
    }, step);
  }

  private startStatusRotation(): void {
    let index = 0;
    this.statusInterval = window.setInterval(() => {
      if (this.progress < 95) {
        this.loadingStatus = this.statusMessages[index % this.statusMessages.length];
        index++;
        this.cdr.detectChanges();
      } else if (this.progress >= 95) {
        this.loadingStatus = 'Finalizando...';
        this.cdr.detectChanges();
      }
    }, 2000);
  }

  private completeLoading(): void {
    if (this.progressInterval) window.clearInterval(this.progressInterval);
    if (this.statusInterval) window.clearInterval(this.statusInterval);
    this.completed.emit();
  }

  private initializeAnimation(): void {
    if (!this.animationContainer) return;

    const container = this.animationContainer.nativeElement;
    container.innerHTML = '';

    const animationData = this.isDarkMode ? nebulaAnimationDark : nebulaAnimationLight;

    this.animation = lottie.loadAnimation({
      container: container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: animationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid meet',
      },
    });

    this.animation.addEventListener('DOMLoaded', () => {
      setTimeout(() => this.fixSVGDisplay(), 50);
    });
  }

  private fixSVGDisplay(): void {
    const container = this.animationContainer.nativeElement;
    const svg = container.querySelector('svg');

    if (!svg) return;

    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.display = 'block';
    svg.setAttribute('viewBox', '0 0 300 300');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  }

  ngOnDestroy(): void {
    if (this.animation) this.animation.destroy();
    if (this.progressInterval) window.clearInterval(this.progressInterval);
    if (this.statusInterval) window.clearInterval(this.statusInterval);
  }
}
